/* Marbury Lane cart offer.

   Drives the row list rendered by snippets/ml-cart-offer.liquid and
   snippets/ml-cart-offer-row.liquid inside the cart drawer: what a row can
   inherit, what it still has to ask, and the add.

   ---------------------------------------------------------------------------
   WHY THIS LOADS GLOBALLY AND NOT FROM ITS OWN SNIPPET

   Every other ML block ships its <script src> at the bottom of its own snippet.
   This one cannot. The offer only renders when the cart holds one or two
   pieces, so on a page that loaded with an EMPTY cart the snippet is absent and
   its script would never load. The shopper then adds a product, the Section
   Rendering API swaps new drawer HTML in via innerHTML - and scripts inserted
   by innerHTML do not execute. The custom element would never be defined and
   the offer would render as dead markup.

   So it is loaded from layout/theme.liquid alongside marbury-bundle.js. Do not
   move it back into the snippet.

   Re-rendered markup still upgrades correctly: assigning innerHTML instantiates
   any already-registered custom element, so connectedCallback fires on every
   drawer re-render without further work.

   ---------------------------------------------------------------------------
   THE INHERIT PAYLOAD IS AN ARRAY OF PAIRS, ON PURPOSE

   An earlier build read `{{ src.properties | json }}` and assumed it parsed to
   { "Name 1": "Mia" }. It does not. Object.assign over an array of pairs yields
   { "0": [...], "1": [...] }, so every piece added through the offer reached
   the cart with properties literally named "0" and "1" and no names at all.

   The snippet now emits [{name, value}, ...] built by hand, and toMap below is
   the ONLY place it becomes an object. Blank values and underscore-prefixed
   names are dropped here so nothing downstream has to think about them.

   ---------------------------------------------------------------------------
   THE GUARD

   A row whose product is declared as needing personalization will NOT add with
   an empty property map. It sends the shopper to the product page instead. An
   uncustomized keepsake is worse than a lost upsell: it is an unfulfillable
   order that looks fulfillable. Fail loud, never silent.

   ---------------------------------------------------------------------------
   ONE TAP IS THE DEFAULT, AND THIS IS WHERE THAT IS DECIDED

   A row adds immediately when nothing is left to ask. Deciding that needs the
   product's real options, so the product JSON is fetched on the FIRST tap and
   cached. If everything resolves, the tap that fetched it is also the tap that
   adds - the shopper sees one interaction, not two.

   Three things can be inherited from the cart:

     PROPERTIES   copied verbatim for a second unit of the same product.
     OPTIONS      matched BY NAME against the cart line, so a gold ring in the
                  cart pre-selects gold on the offered piece. This is what makes
                  a cross-sell one tap rather than a form.
     NOTHING ELSE Size is never inherited. A second ring is for a different
                  person's finger, so any option named in reselectOptions is
                  always asked even when the cart line has a value for it.

   ---------------------------------------------------------------------------
   HOW THE PIECE REACHES THE CART

   POST /cart/add.js with an items array carrying its OWN properties object,
   then hand the returned sections to the existing CartDrawer.renderContents.

   This deliberately does not go through product-form.js or the
   items[n][properties] ladder path. The ladder copies one set of values across
   every line, which is right for a matched set on the PDP and wrong here: the
   entire point of this block is that the second piece can carry different names
   from the first. */

(function () {
  'use strict';

  var WORDS = {
    one: 1, two: 2, three: 3, four: 4, five: 5,
    six: 6, seven: 7, eight: 8, nine: 9, ten: 10
  };

  var MAX_NAMES = 5;

  /* Same rule as ml-name-engraving.js. The live product writes name counts as
     words - "One name", "Three names" - so a digit test alone is not enough. */
  function parseCount(raw) {
    if (raw == null) return NaN;
    var s = String(raw).trim().toLowerCase();
    var digits = s.match(/\d+/);
    if (digits) return parseInt(digits[0], 10);
    var word = s.match(/\b(one|two|three|four|five|six|seven|eight|nine|ten)\b/);
    return word ? WORDS[word[1]] : NaN;
  }

  function parseJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  /* [{name, value}] to {name: value}. The single conversion point, and the only
     place blanks and Shopify's underscore-prefixed private properties are
     filtered. */
  function toMap(pairs) {
    var out = {};
    if (!Array.isArray(pairs)) return out;
    pairs.forEach(function (pair) {
      if (!pair || !pair.name) return;
      var name = String(pair.name);
      if (name.charAt(0) === '_') return;
      var value = pair.value == null ? '' : String(pair.value).trim();
      if (!value) return;
      out[name] = value;
    });
    return out;
  }

  function splitSetting(raw) {
    return String(raw || '')
      .split(',')
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  function optionName(o) {
    return (typeof o === 'string' ? o : (o && o.name) || '').trim();
  }

  /* Product JSON has shipped options as bare strings and as objects across API
     versions. Normalise once so nothing downstream has to care. */
  function optionNames(product) {
    return (product.options || []).map(optionName);
  }

  /* Cached per handle for the life of the page. A shopper who opens, collapses
     and reopens a row should not refetch. */
  var PRODUCTS = {};

  function fetchProduct(handle) {
    if (PRODUCTS[handle]) return Promise.resolve(PRODUCTS[handle]);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('product ' + res.status);
        return res.json();
      })
      .then(function (json) {
        PRODUCTS[handle] = json;
        return json;
      });
  }

  class MlCartOffer extends HTMLElement {
    connectedCallback() {
      this.errorEl = this.querySelector('[data-ml-offer-error]');
      this.off = parseInt(this.dataset.off, 10) || 0;
      this.reselectOptions = splitSetting(this.dataset.reselectOptions).map(function (s) {
        return s.toLowerCase();
      });
      this.monthProps = splitSetting(this.dataset.monthProps);
      this.maxChars = parseInt(this.dataset.maxChars, 10) || 10;
      this.months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      this.busy = false;

      this.bind();
    }

    disconnectedCallback() {
      if (this.clickHandler) this.removeEventListener('click', this.clickHandler);
      if (this.inputHandler) this.removeEventListener('input', this.inputHandler);
      if (this.changeHandler) this.removeEventListener('change', this.changeHandler);
    }

    /* Event delegation on the component root, the house pattern. Panels are
       built after binding, so nothing may listen on an individual field. */
    bind() {
      var self = this;

      this.clickHandler = function (e) {
        var trigger = e.target.closest('[data-ml-offer-trigger]');
        if (trigger) {
          e.preventDefault();
          self.activate(trigger.closest('[data-ml-offer-row]'));
          return;
        }
        var confirmBtn = e.target.closest('[data-ml-offer-confirm]');
        if (confirmBtn) {
          e.preventDefault();
          self.submit(confirmBtn.closest('[data-ml-offer-row]'));
          return;
        }
        var change = e.target.closest('[data-ml-offer-change]');
        if (change) {
          e.preventDefault();
          /* Change can be the very first thing tapped, before any fetch has
             happened, so it has to guarantee the product itself. */
          var changeRow = change.closest('[data-ml-offer-row]');
          self.setRowBusy(changeRow, true);
          fetchProduct(changeRow.dataset.handle)
            .then(function (product) {
              self.setRowBusy(changeRow, false);
              self.expand(changeRow, true, product);
            })
            .catch(function () {
              self.setRowBusy(changeRow, false);
              self.showError('That could not be loaded. Please try again.');
            });
        }
      };
      this.addEventListener('click', this.clickHandler);

      this.changeHandler = function (e) {
        self.clearError();
        if (e.target.matches('[data-ml-offer-option]')) {
          self.onOptionChange(e.target.closest('[data-ml-offer-row]'));
        }
      };
      this.addEventListener('change', this.changeHandler);

      this.inputHandler = function () { self.clearError(); };
      this.addEventListener('input', this.inputHandler);
    }

    /* ------------------------------------------------------------- messaging */

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    /* ------------------------------------------------------------- inherited */

    inherited(row) {
      if (!row._inherit) {
        var raw = parseJSON(row.querySelector('[data-ml-offer-inherit]')) || {};
        row._inherit = {
          properties: toMap(raw.properties),
          options: toMap(raw.options)
        };
      }
      return row._inherit;
    }

    /* Option names are compared case-insensitively, so a cart line's "Color"
       matches an offered product's "color". */
    inheritedOptionMap(row) {
      var map = {};
      var opts = this.inherited(row).options;
      Object.keys(opts).forEach(function (name) {
        map[name.trim().toLowerCase()] = opts[name];
      });
      return map;
    }

    mustAsk(name) {
      return this.reselectOptions.indexOf(String(name).trim().toLowerCase()) !== -1;
    }

    /* True when this product is declared as carrying personalization, which is
       what the guard keys on. */
    needsProperties(row) {
      var kind = row.dataset.declaredKind;
      return kind === 'names' || kind === 'stones';
    }

    /* ------------------------------------------------------------ activation */

    activate(row) {
      var self = this;
      if (this.busy || !row) return;

      if (row.dataset.open === 'true') {
        this.submit(row);
        return;
      }

      this.clearError();
      this.setRowBusy(row, true);

      fetchProduct(row.dataset.handle)
        .then(function (product) {
          self.setRowBusy(row, false);
          var plan = self.plan(row, product);

          /* Nothing left to ask. The tap that fetched the product is the tap
             that adds. */
          if (!plan.needsPanel) {
            self.add(row, plan.variantId, plan.properties);
            return;
          }
          self.expand(row, false, product);
        })
        .catch(function () {
          self.setRowBusy(row, false);
          self.showError('That could not be loaded. Please try again.');
        });
    }

    /* Works out what this row can resolve on its own and what it must ask for.
       Called before deciding whether to show a panel at all, and again to seed
       the panel that does appear. */
    plan(row, product) {
      var self = this;
      var names = optionNames(product);
      var inheritMap = this.inheritedOptionMap(row);
      var kind = row.dataset.kind;

      var values = [];
      var ask = [];

      names.forEach(function (name, i) {
        var wanted = inheritMap[name.toLowerCase()];
        if (!self.mustAsk(name) && wanted) {
          /* Only inherit a value the offered product actually carries. */
          var exists = product.variants.some(function (v) {
            return v.options && v.options[i] === wanted;
          });
          if (exists) {
            values[i] = wanted;
            return;
          }
        }
        values[i] = null;
        ask.push({ index: i, name: name });
      });

      /* A single-variant product has one option named "Title" with value
         "Default Title" and nothing to choose. */
      if (product.variants.length === 1) {
        values = product.variants[0].options.slice();
        ask = [];
      }

      var variantId = this.matchVariant(product, values);
      var properties = kind === 'inherited'
        ? Object.assign({}, this.inherited(row).properties)
        : {};

      var needsFields = kind === 'names' || kind === 'stones';

      /* The guard. An inherited row that resolved no properties has lost them
         somewhere; do not create a bare line. */
      var lostProperties = this.needsProperties(row)
        && kind === 'inherited'
        && Object.keys(properties).length === 0;

      return {
        product: product,
        values: values,
        ask: ask,
        variantId: variantId,
        properties: properties,
        lostProperties: lostProperties,
        needsPanel: ask.length > 0 || needsFields || !variantId || lostProperties
      };
    }

    matchVariant(product, values) {
      if (!values.length) return null;
      if (values.some(function (v) { return !v; })) return null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        var hit = values.every(function (val, idx) { return v.options[idx] === val; });
        if (hit && v.available) return v.id;
      }
      return null;
    }

    /* ---------------------------------------------------------------- panel */

    expand(row, forceFields, product) {
      if (!product) {
        product = PRODUCTS[row.dataset.handle];
        if (!product) return;
      }

      var plan = this.plan(row, product);

      /* Nothing this panel could ask would recover the lost properties, so send
         them where the real fields live. */
      if (plan.lostProperties) {
        this.bailToProduct(row);
        return;
      }

      var panel = row.querySelector('[data-ml-offer-panel]');
      var trigger = row.querySelector('[data-ml-offer-trigger]');

      panel.innerHTML = this.panelHTML(row, plan, forceFields);
      panel.hidden = false;
      row.dataset.open = 'true';
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      this.onOptionChange(row);

      /* Bring the panel into view before focusing. At ml_offer_max_rows 2 the
         expanded row is usually the bottom one, so the panel opens below the
         fold and the tap reads as "nothing happened". focus() keeps
         preventScroll because the scroll it would do is the wrong one: it
         targets the field, not the panel. */
      try { panel.scrollIntoView({ block: 'nearest' }); } catch (e) {}

      var first = panel.querySelector('select, input');
      if (first) {
        try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
      }
      this.clearError();
    }

    bailToProduct(row) {
      var url = row.dataset.url;
      if (url) {
        window.location.href = url;
        return;
      }
      this.showError('Please add this one from its product page.');
    }

    panelHTML(row, plan, forceFields) {
      var self = this;
      var kind = row.dataset.kind;
      var html = '';

      plan.ask.forEach(function (item) {
        var seen = {};
        var opts = plan.product.variants
          .map(function (v) { return v.options[item.index]; })
          .filter(function (v) {
            if (!v || seen[v]) return false;
            seen[v] = true;
            return true;
          });

        var id = 'MlOfferOpt-' + row.dataset.handle + '-' + item.index;
        html += '<div class="ml-offer__field">';
        html += '<label class="ml-offer__label" for="' + id + '">' + self.escape(item.name) + '</label>';
        html += '<select class="ml-offer__select" id="' + id
          + '" data-ml-offer-option="' + item.index + '">';
        html += '<option value="" selected disabled>'
          + self.escape('Choose ' + item.name.toLowerCase()) + '</option>';
        opts.forEach(function (v) {
          html += '<option value="' + self.escape(v) + '">' + self.escape(v) + '</option>';
        });
        html += '</select></div>';
      });

      if (kind === 'stones') {
        this.monthProps.forEach(function (prop, i) {
          var id = 'MlOfferProp-' + row.dataset.handle + '-' + i;
          html += '<div class="ml-offer__field">';
          html += '<label class="ml-offer__label" for="' + id + '">' + self.escape(prop) + '</label>';
          html += '<select class="ml-offer__select" id="' + id
            + '" data-ml-offer-prop="' + self.escape(prop) + '">';
          html += '<option value="" selected disabled>Select a birth month</option>';
          self.months.forEach(function (m) {
            html += '<option value="' + m + '">' + m + '</option>';
          });
          html += '</select></div>';
        });
      }

      if (kind === 'names') {
        html += '<div class="ml-offer__names" data-ml-offer-names></div>';
      }

      /* Editing an inherited row. The property NAMES are already known from the
         cart line, so the field type is decided per property rather than from
         kind: a name listed in monthProps is a month, anything else is text.
         That is what lets one branch serve both the necklace and the ring. */
      if (kind === 'inherited' && forceFields) {
        var inheritedProps = this.inherited(row).properties;
        Object.keys(inheritedProps).forEach(function (prop) {
          var value = inheritedProps[prop];
          var id = 'MlOfferProp-' + row.dataset.handle + '-' + self.slug(prop);

          html += '<div class="ml-offer__field">';
          html += '<label class="ml-offer__label" for="' + id + '">' + self.escape(prop) + '</label>';

          if (self.monthProps.indexOf(prop) !== -1) {
            html += '<select class="ml-offer__select" id="' + id
              + '" data-ml-offer-prop="' + self.escape(prop) + '">';
            self.months.forEach(function (m) {
              html += '<option value="' + m + '"' + (m === value ? ' selected' : '') + '>' + m + '</option>';
            });
            html += '</select>';
          } else {
            html += '<input type="text" class="ml-offer__input" id="' + id
              + '" data-ml-offer-prop="' + self.escape(prop) + '" value="' + self.escape(value)
              + '" maxlength="' + self.maxChars + '" autocomplete="off" spellcheck="false">';
          }
          html += '</div>';
        });
      }

      html += '<div class="ml-offer__actions">';
      html += '<button type="button" class="ml-offer__btn" data-ml-offer-confirm>'
        + this.escape(this.dataset.addLabel || 'Add to order') + '</button>';
      html += '</div>';

      return html;
    }

    /* Name fields follow the CHOSEN VARIANT, because the name count is a
       variant option on this product and not a property. Rebuilt on every
       option change for that reason. */
    renderNames(row) {
      var host = row.querySelector('[data-ml-offer-names]');
      if (!host) return;

      var product = PRODUCTS[row.dataset.handle];
      if (!product) return;

      var count = this.nameCount(row, product);
      var existing = this.inherited(row).properties;
      var current = {};
      host.querySelectorAll('[data-ml-offer-prop]').forEach(function (el) {
        current[el.getAttribute('data-ml-offer-prop')] = el.value;
      });

      var html = '';
      for (var i = 1; i <= count; i++) {
        var prop = 'Name ' + i;
        var value = current[prop] !== undefined ? current[prop] : (existing[prop] || '');
        var id = 'MlOfferName-' + row.dataset.handle + '-' + i;
        html += '<div class="ml-offer__field">';
        html += '<label class="ml-offer__label" for="' + id + '">' + this.escape(prop) + '</label>';
        html += '<input type="text" class="ml-offer__input" id="' + id
          + '" data-ml-offer-prop="' + prop + '" value="' + this.escape(value)
          + '" maxlength="' + this.maxChars + '" autocomplete="off" spellcheck="false">';
        html += '</div>';
      }
      host.innerHTML = html;
    }

    /* Mirrors resolveNameOption in ml-name-engraving.js: find the option whose
       values all read as a count, which is what actually fires on the live
       product where the option is called "style". */
    nameCount(row, product) {
      var names = optionNames(product);
      var idx = -1;
      for (var i = 0; i < names.length; i++) {
        var at = i;
        var allCounts = product.variants.length > 0 && product.variants.every(function (v) {
          var n = parseCount(v.options && v.options[at]);
          return !isNaN(n) && n >= 1 && n <= MAX_NAMES;
        });
        if (allCounts) { idx = at; break; }
      }

      /* No count option on this product at all: every field is always shown. */
      if (idx === -1) return MAX_NAMES;

      var selected = this.selectedValues(row, product);
      var n = parseCount(selected[idx]);

      /* The option exists but has not been chosen yet. Show nothing rather than
         five fields that are about to collapse to two - a form that shrinks
         under you reads as broken. The option change re-renders this. */
      return isNaN(n) ? 0 : n;
    }

    selectedValues(row, product) {
      var values = this.plan(row, product).values.slice();
      row.querySelectorAll('[data-ml-offer-option]').forEach(function (sel) {
        var i = parseInt(sel.getAttribute('data-ml-offer-option'), 10);
        if (sel.value) values[i] = sel.value;
      });
      return values;
    }

    onOptionChange(row) {
      if (row.querySelector('[data-ml-offer-names]')) this.renderNames(row);
    }

    /* --------------------------------------------------------------- submit */

    submit(row) {
      var product = PRODUCTS[row.dataset.handle];
      if (!product) return;

      var values = this.selectedValues(row, product);
      var missingIdx = values.findIndex(function (v) { return !v; });
      if (missingIdx !== -1) {
        var names = optionNames(product);
        this.showError('Choose ' + (names[missingIdx] || 'an option').toLowerCase() + ' first.');
        return;
      }

      var variantId = this.matchVariant(product, values);
      if (!variantId) {
        this.showError('That combination is not available.');
        return;
      }

      var properties = row.dataset.kind === 'inherited'
        ? Object.assign({}, this.inherited(row).properties)
        : {};

      var empty = null;
      row.querySelectorAll('[data-ml-offer-prop]').forEach(function (el) {
        var name = el.getAttribute('data-ml-offer-prop');
        var value = (el.value || '').trim();
        if (!value) { if (!empty) empty = el; return; }
        properties[name] = value;
      });

      if (empty) {
        this.showError(empty.getAttribute('data-ml-offer-prop') + ' cannot be empty.');
        try { empty.focus(); } catch (e) { /* older Safari */ }
        return;
      }

      this.add(row, variantId, properties);
    }

    setRowBusy(row, on) {
      this.busy = on;
      row.classList.toggle('ml-offer__row--busy', on);
      var trigger = row.querySelector('[data-ml-offer-trigger]');
      if (trigger) trigger.disabled = on;
      var confirmBtn = row.querySelector('[data-ml-offer-confirm]');
      if (confirmBtn) confirmBtn.disabled = on;
    }

    add(row, variantId, properties) {
      var self = this;
      if (!variantId) {
        this.showError('That could not be added.');
        return;
      }

      /* The guard, at the last possible moment. Nothing reaches the cart bare. */
      if (this.needsProperties(row) && Object.keys(properties).length === 0) {
        this.bailToProduct(row);
        return;
      }

      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.setRowBusy(row, true);

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: properties }],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname
        })
      })
        .then(function (res) { return res.json(); })
        .then(function (json) {
          /* Shopify answers 422 with a description rather than throwing, so a
             sold-out variant lands here and not in the catch. */
          if (json.status) {
            self.setRowBusy(row, false);
            self.showError(json.description || json.message || 'That could not be added.');
            return;
          }

          var drawer = document.querySelector('cart-drawer');
          if (drawer && json.sections) {
            drawer.renderContents(json);
          } else {
            window.location.reload();
            return;
          }

          document.dispatchEvent(new CustomEvent('ml:offer:added', {
            bubbles: true,
            detail: { variantId: variantId, handle: row.dataset.handle }
          }));
        })
        .catch(function () {
          self.setRowBusy(row, false);
          self.showError('Something went wrong. Please try again.');
        });
    }

    slug(str) {
      return String(str || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }

    escape(str) {
      return String(str == null ? '' : str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }
  }

  if (!customElements.get('ml-cart-offer')) {
    customElements.define('ml-cart-offer', MlCartOffer);
  }
})();
